import { useMemo, useRef } from 'react';
import { useForceRender } from './useForceRender';

/**
 * Manage map.
 *
 * All setters (set, assign,..) are stable.
 * All getters get changed on updates.
 *
 * Uses single object instance under the hood for performance with large maps and frequent assignments.
 *
 * @param {Object} initial
 */
export const useMap = (initial) => {
	const map = useRef({ ...initial });
	const [$render, forceRender] = useForceRender();

	const stableActions = useMemo(
		() => ({
			set: (key, val) => {
				if (map.current[key] !== val) {
					map.current[key] = val;
					forceRender();
				}
			},
			setFn: (key, fn) => {
				const current = map.current[key];
				const val = fn(current);
				if (current !== val) {
					map.current[key] = val;
					forceRender();
				}
			},
			assign: (obj) => {
				let shouldRender = false;
				Object.entries(obj).forEach(([key, val]) => {
					if (map.current[key] !== val) {
						map.current[key] = val;
						shouldRender = true;
					}
				});

				shouldRender && forceRender();
			},
			replace: (obj) => {
				// todo: only rerender if direct values changed
				map.current = { ...obj };
				forceRender();
			},
			remove: (key) => {
				if (key in map.current) {
					delete map.current[key];
					forceRender();
				}
			},
			clear: () => {
				if (Object.keys(map.current).length) {
					map.current = {};
					forceRender();
				}
			},
		}),
		[forceRender],
	);

	const api = useMemo(
		() => ({
			...stableActions,
			get(key, dflt) {
				const res = map.current[key];
				return res === undefined ? dflt : res;
			},
			entries: () => Object.entries(map.current),
			values: () => Object.values(map.current),
			keys: () => Object.keys(map.current),
			$render,
		}),
		[$render, stableActions],
	);

	return api;
};
